import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  WorkspaceChrome,
  WorkspaceSummary,
  WorkspaceActions,
  WorkspaceSection,
  WorkspaceStat,
  tabFromQuery,
} from '../../../platform/workspace360';
import RecordNavigation from '../../../components/common/recordNavigation';
import CustomerPaymentModal from '../../../components/payment/CustomerPaymentModal';
import { downloadReceiptPdf } from '../../../services/invoiceService';
import { paymentProviderLabel } from '../../../utils/providerDisplay';
import { toast } from 'react-toastify';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'related', label: 'Related' },
];

const STATUS_LABELS = {
  0: 'Pending',
  1: 'Payment confirmed',
  2: 'Applied / completed',
  3: 'Cancelled',
  4: 'Abandoned',
  5: 'Expired',
  6: 'Failed',
};

const PAYMODE_LABELS = {
  1: 'Cash',
  2: 'Bank',
  3: 'Credit',
  4: 'Online',
};

function formatMoney(value) {
  const amount = Number(value || 0);
  return Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(amount);
}

class Payment360Panel extends Component {
  state = {
    activeTab: 'overview',
    paying: false,
  };

  componentDidMount() {
    this.syncTab();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location?.search !== this.props.location?.search) {
      this.syncTab();
    }
  }

  syncTab = () => {
    this.setState({
      activeTab: tabFromQuery(this.props.location?.search, 'overview', TABS),
    });
  };

  handleTabChange = (tabId) => {
    this.setState({ activeTab: tabId });
    const search = tabId === 'overview' ? '' : `?tab=${tabId}`;
    this.props.history.replace({ pathname: this.props.location.pathname, search });
  };

  handleDownloadReceipt = async () => {
    const { payment } = this.props;
    try {
      await downloadReceiptPdf(payment.transid, `receipt-${payment.transid}.pdf`);
    } catch (_) {
      toast.error('Failed to download receipt.');
    }
  };

  renderOverview() {
    const { payment } = this.props;
    return (
      <WorkspaceSection title="Overview" icon="fa-credit-card">
        <div className="customer360-grid-3">
          <WorkspaceStat label="Transaction ID" value={payment.transid} isStatic />
          <WorkspaceStat label="Status" value={STATUS_LABELS[payment.status] || payment.status} isStatic />
          <WorkspaceStat label="Amount" value={formatMoney(payment.amount)} isStatic />
          <WorkspaceStat label="Account" value={payment.account} isStatic />
          <WorkspaceStat label="Paid" value={payment.paid} isStatic />
          <WorkspaceStat label="Pay mode" value={Number(payment.paymode) === 4 ? `Online · ${paymentProviderLabel(payment)}` : (PAYMODE_LABELS[payment.paymode] || payment.paymode)} isStatic />
          <WorkspaceStat label="Invoice #" value={payment.invnum} isStatic />
          <WorkspaceStat label="Email" value={payment.email} isStatic />
          <WorkspaceStat label="Quantity" value={payment.quantity} isStatic />
          <WorkspaceStat label="Unit price" value={formatMoney(payment.unitprice)} isStatic />
        </div>
        {payment.remark ? (
          <div className="mt-3">
            <div className="customer360-snapshot__stat-label">Remark</div>
            <div>{payment.remark}</div>
          </div>
        ) : null}
      </WorkspaceSection>
    );
  }

  renderRelated() {
    const { payment } = this.props;
    return (
      <WorkspaceSection title="Related" icon="fa-link">
        <div className="customer360-grid-3">
          <WorkspaceStat
            label="Account"
            value={
              payment.account ? (
                <Link to={`/accounts/${payment.account}`}>{payment.account}</Link>
              ) : null
            }
            isStatic
          />
          <WorkspaceStat label="Invoice #" value={payment.invnum} isStatic />
        </div>
      </WorkspaceSection>
    );
  }

  render() {
    const {
      payment,
      history,
      location,
      navPaths,
      providerPublicKey,
      onRefresh,
      onSync,
    } = this.props;
    if (!payment || !payment.transid) return null;
    const { activeTab } = this.state;

    // Pay Now is a fresh explicit attempt on an unsettled surface. Hide it
    // once the attempt is settled: status 1 (PAYMENT_CONFIRMED) or 2
    // (APPLIED_COMPLETED), or whenever a paid timestamp is recorded.
    const showPayNow = !!payment.transid && !payment.paid && ![1, 2].includes(Number(payment.status));
    const showRequery = !!payment.transid && String(payment.paymode) === '4';
    const showReceipt = !!payment.paid;

    const summary = (
      <WorkspaceSummary
        title="Payment Overview"
        icon="fa-money"
        stats={[
          {
            key: 'status',
            label: 'Status',
            value: STATUS_LABELS[payment.status] || '—',
            onClick: () => this.handleTabChange('overview'),
            isActive: activeTab === 'overview',
          },
          {
            key: 'amount',
            label: 'Amount',
            value: formatMoney(payment.amount),
            onClick: () => this.handleTabChange('overview'),
            isActive: activeTab === 'overview',
          },
          {
            key: 'account',
            label: 'Account',
            value: payment.account || '—',
            onClick: () => this.handleTabChange('related'),
            isActive: activeTab === 'related',
          },
        ]}
      />
    );

    const actions = (
      <WorkspaceActions>
        {showPayNow ? (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => this.setState({ paying: true })}
          >
            <i className="fa fa-credit-card mr-1" aria-hidden /> Pay Now
          </button>
        ) : null}
        {this.state.paying ? (
          <CustomerPaymentModal
            payment={payment}
            invoice={payment.invoiceid ? { id: payment.invoiceid, invoicenum: payment.invnum, balance: payment.amount } : undefined}
            accountId={!payment.invoiceid ? payment.accountId : undefined}
            quantity={payment.quantity}
            title={payment.invoiceid ? `Pay invoice ${payment.invnum}` : 'Pay subscription'}
            onClose={() => this.setState({ paying: false })}
            onPaid={() => {
              this.setState({ paying: false });
              onRefresh(payment.transid);
            }}
          />
        ) : null}
        {showRequery ? (
          <button type="button" className="btn btn-light btn-sm" onClick={onSync}>
            <i className="fa fa-refresh mr-1" aria-hidden /> Sync
          </button>
        ) : null}
        {showReceipt ? (
          <button
            type="button"
            className="btn btn-light btn-sm"
            onClick={this.handleDownloadReceipt}
          >
            <i className="fa fa-file-pdf-o mr-1" aria-hidden /> Receipt
          </button>
        ) : null}
      </WorkspaceActions>
    );

    return (
      <WorkspaceChrome
        title={payment.transid}
        subtitle={[STATUS_LABELS[payment.status], formatMoney(payment.amount)]
          .filter(Boolean)
          .join(' · ')}
        listLabel="Payments"
        listTo="/payments"
        recordLabel={payment.transid}
        nav={
          <RecordNavigation
            history={history}
            currentPath={location.pathname}
            paths={navPaths || []}
          />
        }
        summary={summary}
        actions={actions}
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={this.handleTabChange}
      >
        {activeTab === 'related' ? this.renderRelated() : this.renderOverview()}
      </WorkspaceChrome>
    );
  }
}

Payment360Panel.propTypes = {
  payment: PropTypes.object,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  navPaths: PropTypes.array,
  providerPublicKey: PropTypes.string,
  onRefresh: PropTypes.func,
  onSync: PropTypes.func,
};

export default Payment360Panel;
